import { useMutation } from "@apollo/client"
import type { ChallengeVotesInput } from "@artists/gql/design-challenges"
import {
	UpdateVotesDocument,
	VotesDocument,
} from "@artists/gql/design-challenges"
import { useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"

import { DEFAULT_ENTRIES_LIMIT } from "../../../../api/hooks/vote"
import { Root, useAnalytics } from "../../../../utils"
import { useOffset } from "../offset-context"
import { useUnsavedWarning } from "../unsaved-context"
import { useVoting } from "../vote-context"

interface UseSubmitProps {
	slug: string
	challengeId: string
}

export const useSubmit = ({ challengeId, slug }: UseSubmitProps) => {
	const setDirty = useUnsavedWarning()
	const navigate = useNavigate()
	const { voted, unvoted, resetChanges } = useVoting()
	const { paginationOffset } = useOffset()
	const [updateVotes, { loading: isUpdating, error }] = useMutation(
		UpdateVotesDocument,
		{
			refetchQueries: [VotesDocument],
		},
	)
	const { send } = useAnalytics()

	const numericOffset = paginationOffset ?? 0

	const submitDto = useRef({} as Omit<ChallengeVotesInput, "captchaToken">)

	submitDto.current = {
		challengeId,
		selectedEntriesIds: [...voted],
		unselectedEntriesIds: [...unvoted],
		currentOffset: numericOffset + DEFAULT_ENTRIES_LIMIT,
	}

	const onSubmit = useCallback(
		async (captchaToken?: Maybe<string>) => {
			setDirty(false)

			const updateVotesDto: ChallengeVotesInput = {
				...submitDto.current,
				captchaToken: captchaToken ?? null,
			}

			await updateVotes({
				variables: { updateVotesDto },
				update: cache => {
					cache.evict({
						id: "ROOT_QUERY",
						fieldName: "challengeEntriesForVoting",
					})
					cache.evict({
						id: "ROOT_QUERY",
						fieldName: "myVotingResults",
					})
					cache.evict({
						id: "ROOT_QUERY",
						fieldName: "myVotedChallenges",
					})
					cache.gc()
				},
			})

			send({
				event: "voteSubmitted",
				count: updateVotesDto.selectedEntriesIds.length,
				pageNumber: Math.ceil(
					numericOffset / DEFAULT_ENTRIES_LIMIT + 1,
				),
			})

			resetChanges()
			navigate(Root.ReviewVotes.buildPath({ slug }))
		},
		[
			navigate,
			numericOffset,
			resetChanges,
			send,
			setDirty,
			slug,
			updateVotes,
		],
	)

	return { onSubmit, isUpdating, error }
}
